import React, { useState, useEffect } from 'react'
import { toastError, toastSuccess, toastLoading, toastDismiss } from '../../utils/toastHelper'
import { useTheme } from '../../hooks/useTheme'
import { useApiClient } from '../../hooks/useApiClient'
import { formatDateDisplay, parseLocalDateString } from '../../utils/dateHelpers'
import type { Reserva } from '../../hooks/useReservationsAdmin'

interface CambiarReservaModalProps {
  reserva: Reserva
  isOpen: boolean
  onClose: () => void
  onSuccess: () => void
}

interface CambioReservaData {
  espacio_id_a_cambiar?: string  // Nuevo: ID del espacio que se quiere cambiar
  nuevo_espacio_id?: string
  nueva_fecha_entrada?: string
  nueva_fecha_salida?: string
  enviar_notificacion: boolean
  motivo_cambio?: string
}

interface EspacioAdmin {
  id: string
  nombre: string
  codigo: string
  estado: string
}

const CambiarReservaModal: React.FC<CambiarReservaModalProps> = ({
  reserva,
  isOpen,
  onClose,
  onSuccess,
}) => {
  const { colors } = useTheme()
  const { apiClient } = useApiClient()

  const [espacios, setEspacios] = useState<EspacioAdmin[]>([])
  const [espaciosLoading, setEspaciosLoading] = useState(false)
  const [espacioACambiarId, setEspacioACambiarId] = useState<string>('')  // Nuevo: espacio a cambiar
  const [nuevoEspacioId, setNuevoEspacioId] = useState<string>('')
  const [nuevaFechaEntrada, setNuevaFechaEntrada] = useState<string>('')
  const [nuevaFechaSalida, setNuevaFechaSalida] = useState<string>('')
  const [enviarNotificacion, setEnviarNotificacion] = useState<boolean>(false)
  const [motivoCambio, setMotivoCambio] = useState<string>('')
  const [isSubmitting, setIsSubmitting] = useState(false)

  // Manejar ESC para cerrar modal (con mayor prioridad que el modal padre)
  useEffect(() => {
    if (!isOpen) return

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault()
        e.stopPropagation()
        onClose()
      }
    }

    // Agregar listener con capture=true para tener prioridad sobre el modal padre
    window.addEventListener('keydown', handleKeyDown, true)
    return () => window.removeEventListener('keydown', handleKeyDown, true)
  }, [isOpen, onClose])

  // Cargar espacios al abrir el modal
  useEffect(() => {
    if (isOpen) {
      const fetchEspacios = async () => {
        try {
          setEspaciosLoading(true)
          const response = await apiClient.get('/api/admin-protected/espacios')
          setEspacios(response.data || [])
        } catch (error) {
          toastError('Error al cargar lista de espacios')
        } finally {
          setEspaciosLoading(false)
        }
      }

      fetchEspacios()
    }
  }, [isOpen, apiClient])

  // Reset al abrir/cerrar
  useEffect(() => {
    if (isOpen) {
      // Si la reserva tiene múltiples espacios, no preseleccionar ninguno
      // Si tiene solo 1 espacio, preseleccionar automáticamente
      if (reserva.espacios && reserva.espacios.length === 1) {
        setEspacioACambiarId(reserva.espacios[0].id)
      } else {
        setEspacioACambiarId('')
      }
      setNuevoEspacioId('')
      setNuevaFechaEntrada('')
      setNuevaFechaSalida('')
      setEnviarNotificacion(false)
      setMotivoCambio('')
    }
  }, [isOpen, reserva])

  if (!isOpen) return null

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    // Si la reserva tiene múltiples espacios, validar que se haya seleccionado cuál cambiar
    if (reserva.espacios && reserva.espacios.length > 1 && !espacioACambiarId) {
      toastError('Debe seleccionar qué espacio desea cambiar')
      return
    }

    // Validar que al menos algo cambie
    if (!nuevoEspacioId && !nuevaFechaEntrada && !nuevaFechaSalida) {
      toastError('Debe especificar al menos un cambio: espacio, fecha de entrada o fecha de salida')
      return
    }

    // Validar fechas si se proporcionan
    if (nuevaFechaEntrada || nuevaFechaSalida) {
      const entrada = nuevaFechaEntrada || reserva.fecha_entrada
      const salida = nuevaFechaSalida || reserva.fecha_salida

      const fechaEntradaObj = parseLocalDateString(entrada)
      const fechaSalidaObj = parseLocalDateString(salida)

      if (fechaSalidaObj < fechaEntradaObj) {
        toastError('La fecha de salida debe ser posterior o igual a la fecha de entrada')
        return
      }
    }

    const toastId = toastLoading('Procesando cambio de reserva...')

    try {
      setIsSubmitting(true)

      const payload: CambioReservaData = {
        enviar_notificacion: enviarNotificacion,
      }

      // Si la reserva tiene múltiples espacios, especificar cuál cambiar
      if (reserva.espacios && reserva.espacios.length > 1 && espacioACambiarId) {
        payload.espacio_id_a_cambiar = espacioACambiarId
      }

      if (nuevoEspacioId) {
        payload.nuevo_espacio_id = nuevoEspacioId
      }

      if (nuevaFechaEntrada) {
        payload.nueva_fecha_entrada = nuevaFechaEntrada
      }

      if (nuevaFechaSalida) {
        payload.nueva_fecha_salida = nuevaFechaSalida
      }

      if (motivoCambio.trim()) {
        payload.motivo_cambio = motivoCambio.trim()
      }

      const response = await apiClient.post(
        `/api/admin-protected/reservas/${reserva.id}/cambiar`,
        payload
      )

      toastDismiss(toastId)

      if (response.data.success) {
        toastSuccess(
          `Reserva cambiada exitosamente. ${
            response.data.email_enviado ? 'Email enviado al cliente.' : ''
          }`
        )
        onSuccess()
        onClose()
      } else {
        toastError(response.data.message || 'Error al cambiar reserva')
      }
    } catch (error: any) {
      toastDismiss(toastId)

      if (error.response?.status === 400 || error.response?.status === 409) {
        // Errores de validación o conflicto (tarifa diferente, espacio ocupado)
        toastError(error.response.data.detail || 'Error al cambiar reserva')
      } else {
        toastError('Error al cambiar reserva. Por favor intenta de nuevo.')
      }
    } finally {
      setIsSubmitting(false)
    }
  }

  // Determinar si la reserva tiene múltiples espacios
  const tieneMultiplesEspacios = reserva.espacios && reserva.espacios.length > 1

  // Obtener el espacio actualmente seleccionado para cambiar
  const espacioSeleccionado = tieneMultiplesEspacios
    ? reserva.espacios?.find((e) => e.id === espacioACambiarId)
    : reserva.espacios?.[0]

  const espacioActual = espacioSeleccionado
    ? `${espacioSeleccionado.nombre} (${espacioSeleccionado.codigo || 'N/A'})`
    : reserva.espacio_nombre || 'Sin espacio'

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] flex flex-col overflow-hidden">
        {/* Header */}
        <div
          className="px-6 py-4 flex items-center justify-between text-white flex-shrink-0"
          style={{
            background: `linear-gradient(to right, ${colors.primary}, ${colors.primaryDark})`,
          }}
        >
          <h2 className="text-lg font-bold">🔄 Cambiar Reserva</h2>
          <button
            onClick={onClose}
            className="text-white hover:text-gray-200 transition-colors"
            aria-label="Cerrar"
          >
            <svg
              className="w-6 h-6"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        {/* Body - Scrollable */}
        <form onSubmit={handleSubmit} className="flex flex-col flex-1 overflow-hidden">
          <div className="p-6 space-y-6 overflow-y-auto flex-1">
          {/* Selección de espacio a cambiar (solo para reservas multi-espacio) */}
          {tieneMultiplesEspacios && (
            <div className="bg-purple-50 border-l-4 border-purple-500 p-4 rounded">
              <h3 className="font-semibold text-purple-900 mb-3">
                🔍 Seleccionar Espacio a Cambiar
              </h3>
              <p className="text-sm text-purple-800 mb-3">
                Esta reserva tiene <strong>{reserva.espacios?.length} espacios</strong>. Selecciona cuál deseas cambiar:
              </p>
              <select
                value={espacioACambiarId}
                onChange={(e) => setEspacioACambiarId(e.target.value)}
                className="w-full px-3 py-2 border border-purple-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent bg-white"
                required
              >
                <option value="">-- Seleccionar espacio --</option>
                {reserva.espacios?.map((espacio) => (
                  <option key={espacio.id} value={espacio.id}>
                    {espacio.nombre} ({espacio.codigo || 'N/A'}) - {' '}
                    {espacio.fecha_entrada ? formatDateDisplay(espacio.fecha_entrada) : formatDateDisplay(reserva.fecha_entrada)} →{' '}
                    {espacio.fecha_salida ? formatDateDisplay(espacio.fecha_salida) : formatDateDisplay(reserva.fecha_salida)}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Información actual */}
          <div className="bg-blue-50 border-l-4 border-blue-500 p-4 rounded">
            <h3 className="font-semibold text-blue-900 mb-2">
              📋 {tieneMultiplesEspacios ? 'Espacio Seleccionado' : 'Información Actual'}
            </h3>
            {espacioSeleccionado ? (
              <div className="text-sm text-blue-800 space-y-1">
                <p>
                  <strong>Reserva:</strong> #{reserva.id}
                </p>
                <p>
                  <strong>Espacio:</strong> {espacioActual}
                </p>
                <p>
                  <strong>Fechas:</strong> {' '}
                  {espacioSeleccionado.fecha_entrada ? formatDateDisplay(espacioSeleccionado.fecha_entrada) : formatDateDisplay(reserva.fecha_entrada)} →{' '}
                  {espacioSeleccionado.fecha_salida ? formatDateDisplay(espacioSeleccionado.fecha_salida) : formatDateDisplay(reserva.fecha_salida)}
                </p>
                <p>
                  <strong>Tarifa:</strong> ${espacioSeleccionado.tarifa_unitaria?.toLocaleString('es-CL') || reserva.monto_total.toLocaleString('es-CL')}
                </p>
              </div>
            ) : (
              <p className="text-sm text-blue-800">
                {tieneMultiplesEspacios ? 'Selecciona un espacio arriba para ver su información' : 'Sin información'}
              </p>
            )}
          </div>

          {/* Advertencia importante */}
          <div className="bg-yellow-50 border-l-4 border-yellow-500 p-4 rounded">
            <h3 className="font-semibold text-yellow-900 mb-2">
              ⚠️ Importante
            </h3>
            <p className="text-sm text-yellow-800">
              La tarifa recalculada con los nuevos datos <strong>debe ser exactamente igual</strong>{' '}
              a la tarifa {tieneMultiplesEspacios ? 'del espacio seleccionado' : 'original'}
              {espacioSeleccionado?.tarifa_unitaria && tieneMultiplesEspacios
                ? ` ($${espacioSeleccionado.tarifa_unitaria.toLocaleString('es-CL')})`
                : ` ($${reserva.monto_total.toLocaleString('es-CL')})`
              }. Si hay diferencia, el cambio será rechazado.
            </p>
          </div>

          {/* Nuevo Espacio */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Nuevo Espacio (opcional)
            </label>
            <select
              value={nuevoEspacioId}
              onChange={(e) => setNuevoEspacioId(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              disabled={espaciosLoading}
            >
              <option value="">
                {espaciosLoading ? 'Cargando espacios...' : '-- Mantener espacio actual --'}
              </option>
              {espacios.map((espacio) => (
                <option key={espacio.id} value={espacio.id}>
                  {espacio.codigo} - {espacio.nombre}
                </option>
              ))}
            </select>
          </div>

          {/* Nueva Fecha Entrada */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Nueva Fecha de Entrada (opcional)
            </label>
            <input
              type="date"
              value={nuevaFechaEntrada}
              onChange={(e) => setNuevaFechaEntrada(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          {/* Nueva Fecha Salida */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Nueva Fecha de Salida (opcional)
            </label>
            <input
              type="date"
              value={nuevaFechaSalida}
              onChange={(e) => setNuevaFechaSalida(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          {/* Motivo del Cambio */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Motivo del Cambio (opcional)
            </label>
            <textarea
              value={motivoCambio}
              onChange={(e) => setMotivoCambio(e.target.value)}
              rows={3}
              placeholder="Ej: Cliente solicitó cambio de fechas por conflicto de agenda..."
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
            />
          </div>

          {/* Enviar Notificación */}
          <div className="flex items-center">
            <input
              type="checkbox"
              id="enviar_notificacion"
              checked={enviarNotificacion}
              onChange={(e) => setEnviarNotificacion(e.target.checked)}
              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
            />
            <label
              htmlFor="enviar_notificacion"
              className="ml-2 block text-sm text-gray-700"
            >
              Enviar email de notificación al cliente
            </label>
          </div>
          </div>

          {/* Footer con botones - Siempre visible */}
          <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex-shrink-0">
            <div className="flex justify-end gap-3">
              <button
                type="button"
                onClick={onClose}
                disabled={isSubmitting}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Cancelar
              </button>
              <button
                type="submit"
                disabled={isSubmitting}
                className="px-4 py-2 text-sm font-medium text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                style={{ backgroundColor: colors.primary }}
                onMouseEnter={(e) =>
                  !isSubmitting &&
                  (e.currentTarget.style.backgroundColor = colors.primaryDark)
                }
                onMouseLeave={(e) =>
                  (e.currentTarget.style.backgroundColor = colors.primary)
                }
              >
                {isSubmitting ? '⏳ Procesando...' : '✓ Confirmar Cambio'}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  )
}

export default CambiarReservaModal
